import { FechaCR } from '@/components/dominio';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useActualizarEnsayoEsclerometro,
  useActualizarRebote,
  useCrearRebote,
  useEliminarEnsayoEsclerometro,
  useEliminarRebote,
  useEnsayoEsclerometro,
} from '@/hooks/useEsclerometro';
import { esAdmin, puedeEscribirLab } from '@/lib/cliente';
import { ANGULOS_IMPACTO, type Rebote, calcularReboteUtilPromedio } from '@adelante/shared';
import { ArrowLeft, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * `/lab/esclerometro/:id` — detalle de un ensayo de esclerómetro.
 *
 *  - Header: información básica del ensayo + botón "Editar" (modal inline)
 *  - Tabla de rebotes editable: cada celda tiene foco y se guarda al salir
 *  - Fila final "+ Agregar golpe" con autonumeración
 *  - Promedio visible (descarta max/min ≥3 golpes)
 *
 * Solo lab/admin pueden escribir; admin puede eliminar el ensayo.
 */

const ETIQUETAS_ANGULO: Record<number, string> = {
  '-90': '↓ Hacia abajo (-90°)',
  '-45': '↘ Diagonal abajo (-45°)',
  '0': '→ Horizontal (0°)',
  '45': '↗ Diagonal arriba (45°)',
  '90': '↑ Hacia arriba (90°)',
};

const SKELETON_FILAS = ['s-a', 's-b', 's-c'] as const;
const SKELETON_COLS = ['c0', 'c1', 'c2', 'c3'] as const;

export function EsclerometroDetallePantalla() {
  const { id } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const location = useLocation();
  const idNum = id && /^\d+$/.test(id) ? Number(id) : null;

  const consulta = useEnsayoEsclerometro(idNum);
  const actualizar = useActualizarEnsayoEsclerometro();
  const eliminar = useEliminarEnsayoEsclerometro();
  const crearReb = useCrearRebote();
  const actualizarReb = useActualizarRebote();
  const eliminarReb = useEliminarRebote();

  const puedeEscribir = puedeEscribirLab();
  const puedeEliminar = esAdmin();
  const ensayo = consulta.data?.data ?? null;

  const [modoEdicion, setModoEdicion] = useState(false);
  const [editFecha, setEditFecha] = useState('');
  const [editElemento, setEditElemento] = useState('');
  const [editEdad, setEditEdad] = useState('');
  const [editAngulo, setEditAngulo] = useState<number>(0);
  const [editEquipo, setEditEquipo] = useState('');
  const [editIdCasa, setEditIdCasa] = useState('');
  const [editNotas, setEditNotas] = useState('');

  // Inputs para agregar nuevo rebote
  const [nuevoValor, setNuevoValor] = useState('');
  const [nuevoNotas, setNuevoNotas] = useState('');

  const irAtras = () => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) navegar(from);
    else navegar('/lab/esclerometro');
  };

  const abrirEdicion = () => {
    if (!ensayo) return;
    setEditFecha(ensayo.fecha.slice(0, 10));
    setEditElemento(ensayo.elemento_estructural);
    setEditEdad(ensayo.edad_dias === null ? '' : String(ensayo.edad_dias));
    setEditAngulo(ensayo.angulo_impacto);
    setEditEquipo(ensayo.equipo_serial ?? '');
    setEditIdCasa(ensayo.id_casa ?? '');
    setEditNotas(ensayo.notas ?? '');
    setModoEdicion(true);
  };

  const guardarEdicion = () => {
    if (!ensayo) return;
    const edadNum = editEdad.trim() === '' ? null : Number(editEdad);
    if (edadNum !== null && (!Number.isInteger(edadNum) || edadNum <= 0 || edadNum > 3650)) {
      toast.error('Edad inválida');
      return;
    }
    actualizar.mutate(
      {
        id: ensayo.id,
        fecha: editFecha,
        elemento_estructural: editElemento.trim(),
        edad_dias: edadNum,
        angulo_impacto: editAngulo,
        equipo_serial: editEquipo.trim() || null,
        id_casa: editIdCasa.trim() || null,
        notas: editNotas.trim() || null,
      },
      { onSuccess: (r) => r.data && setModoEdicion(false) },
    );
  };

  // Próximo número de golpe: max(numero_golpe) + 1 o 1 si no hay
  const proximoNumeroGolpe = useMemo(() => {
    if (!ensayo || ensayo.rebotes.length === 0) return 1;
    return Math.max(...ensayo.rebotes.map((r) => r.numero_golpe)) + 1;
  }, [ensayo]);

  const promedio = useMemo(() => {
    if (!ensayo) return null;
    return calcularReboteUtilPromedio(ensayo.rebotes.map((r) => r.valor_rebote));
  }, [ensayo]);

  const promedioBruto = useMemo(() => {
    if (!ensayo || ensayo.rebotes.length === 0) return null;
    return ensayo.rebotes.reduce((s, r) => s + r.valor_rebote, 0) / ensayo.rebotes.length;
  }, [ensayo]);

  const agregarRebote = () => {
    if (!ensayo) return;
    const v = Number(nuevoValor);
    if (!Number.isFinite(v) || v < 0 || v > 100) {
      toast.error('Valor de rebote inválido (0-100)');
      return;
    }
    crearReb.mutate(
      {
        idEnsayo: ensayo.id,
        numero_golpe: proximoNumeroGolpe,
        valor_rebote: v,
        notas: nuevoNotas.trim() || null,
      },
      {
        onSuccess: (r) => {
          if (r.data) {
            setNuevoValor('');
            setNuevoNotas('');
            // Re-foco al input de valor para tipear el siguiente
            queueMicrotask(() => {
              const el = document.getElementById('nuevo-valor') as HTMLInputElement | null;
              el?.focus();
            });
          }
        },
      },
    );
  };

  const onEliminarEnsayo = () => {
    if (!ensayo) return;
    if (!confirm(`¿Eliminar el ensayo #${ensayo.numero}? Se borran también los rebotes.`)) return;
    eliminar.mutate(
      { id: ensayo.id },
      {
        onSuccess: (r) => r.data && navegar('/lab/esclerometro'),
      },
    );
  };

  if (consulta.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (consulta.error || !ensayo) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={irAtras} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Volver
        </Button>
        <p className="text-sm text-destructive">
          {consulta.error?.message ?? 'No se encontró el ensayo.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={irAtras} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Volver al listado
        </Button>
        <div className="flex gap-2">
          {puedeEscribir && !modoEdicion && (
            <Button variant="outline" size="sm" onClick={abrirEdicion} className="gap-1.5">
              <Pencil className="h-4 w-4" /> Editar
            </Button>
          )}
          {puedeEliminar && (
            <Button
              variant="outline"
              size="sm"
              onClick={onEliminarEnsayo}
              disabled={eliminar.isPending}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" /> Eliminar
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-3 text-xl">
            <span className="font-mono">Ensayo #{ensayo.numero}</span>
            <FechaCR fecha={ensayo.fecha} formato="corto" className="text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!modoEdicion && (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <Campo label="Obra">
                {ensayo.obra_works_no ? (
                  <span>
                    <span className="font-mono">{ensayo.obra_works_no}</span>
                    {ensayo.obra_display_name && (
                      <span className="text-muted-foreground"> — {ensayo.obra_display_name}</span>
                    )}
                  </span>
                ) : (
                  <span className="italic text-muted-foreground">—</span>
                )}
              </Campo>
              <Campo label="Casa / ubicación">
                {ensayo.id_casa || <span className="italic text-muted-foreground">—</span>}
              </Campo>
              <Campo label="Elemento estructural">{ensayo.elemento_estructural}</Campo>
              <Campo label="Edad (días)">
                {ensayo.edad_dias ?? <span className="italic text-muted-foreground">—</span>}
              </Campo>
              <Campo label="Ángulo de impacto">
                {ETIQUETAS_ANGULO[ensayo.angulo_impacto] ?? `${ensayo.angulo_impacto}°`}
              </Campo>
              <Campo label="Equipo">
                {ensayo.equipo_serial || <span className="italic text-muted-foreground">—</span>}
              </Campo>
              {ensayo.notas && (
                <div className="sm:col-span-2 lg:col-span-3">
                  <dt className="text-xs text-muted-foreground">Notas</dt>
                  <dd className="whitespace-pre-wrap text-sm">{ensayo.notas}</dd>
                </div>
              )}
            </dl>
          )}

          {modoEdicion && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div className="grid gap-1.5">
                <Label htmlFor="e-fecha">Fecha</Label>
                <Input
                  id="e-fecha"
                  type="date"
                  value={editFecha}
                  onChange={(e) => setEditFecha(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="e-elemento">Elemento estructural</Label>
                <Input
                  id="e-elemento"
                  value={editElemento}
                  onChange={(e) => setEditElemento(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="e-edad">Edad (días)</Label>
                <Input
                  id="e-edad"
                  type="number"
                  value={editEdad}
                  onChange={(e) => setEditEdad(e.target.value)}
                  min={1}
                  max={3650}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="e-angulo">Ángulo</Label>
                <Select value={String(editAngulo)} onValueChange={(v) => setEditAngulo(Number(v))}>
                  <SelectTrigger id="e-angulo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ANGULOS_IMPACTO.map((a) => (
                      <SelectItem key={a} value={String(a)}>
                        {ETIQUETAS_ANGULO[a] ?? `${a}°`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="e-equipo">Equipo (serial)</Label>
                <Input
                  id="e-equipo"
                  value={editEquipo}
                  onChange={(e) => setEditEquipo(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="e-casa">Casa / ubicación</Label>
                <Input
                  id="e-casa"
                  value={editIdCasa}
                  onChange={(e) => setEditIdCasa(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5 sm:col-span-2 lg:col-span-3">
                <Label htmlFor="e-notas">Notas</Label>
                <textarea
                  id="e-notas"
                  value={editNotas}
                  onChange={(e) => setEditNotas(e.target.value)}
                  className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="flex gap-2 sm:col-span-2 lg:col-span-3">
                <Button
                  onClick={guardarEdicion}
                  disabled={actualizar.isPending}
                  className="gap-1.5"
                >
                  <Check className="h-4 w-4" />
                  {actualizar.isPending ? 'Guardando…' : 'Guardar cambios'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setModoEdicion(false)}
                  disabled={actualizar.isPending}
                  className="gap-1.5"
                >
                  <X className="h-4 w-4" /> Cancelar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Rebotes ({ensayo.rebotes.length})</CardTitle>
          <div className="flex flex-col items-end text-xs">
            {promedio !== null && (
              <span className="font-mono text-sm font-medium">R̄ útil: {promedio.toFixed(2)}</span>
            )}
            {promedioBruto !== null && ensayo.rebotes.length >= 3 && (
              <span className="text-muted-foreground">
                (bruto {promedioBruto.toFixed(2)} con todos los golpes)
              </span>
            )}
            {ensayo.rebotes.length < 3 && ensayo.rebotes.length > 0 && (
              <span className="text-amber-700">
                Se recomiendan ≥ 3 golpes para descartar outliers.
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Golpe</TableHead>
                <TableHead className="w-[140px]">Valor R</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead className="w-[60px] text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {consulta.isLoading &&
                SKELETON_FILAS.map((idFila) => (
                  <TableRow key={idFila}>
                    {SKELETON_COLS.map((idCol) => (
                      <TableCell key={`${idFila}-${idCol}`}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {ensayo.rebotes.length === 0 && !puedeEscribir && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                    Sin rebotes registrados.
                  </TableCell>
                </TableRow>
              )}
              {ensayo.rebotes
                .slice()
                .sort((a, b) => a.numero_golpe - b.numero_golpe)
                .map((r) => (
                  <FilaRebote
                    key={r.id}
                    rebote={r}
                    idEnsayo={ensayo.id}
                    puedeEscribir={puedeEscribir}
                    onActualizar={actualizarReb.mutate}
                    onEliminar={() => eliminarReb.mutate({ id: r.id, idEnsayo: ensayo.id })}
                  />
                ))}
              {puedeEscribir && (
                <TableRow className="bg-muted/30">
                  <TableCell className="font-mono text-sm font-medium">
                    {proximoNumeroGolpe}
                  </TableCell>
                  <TableCell>
                    <Input
                      id="nuevo-valor"
                      type="number"
                      step="0.1"
                      min={0}
                      max={100}
                      value={nuevoValor}
                      onChange={(e) => setNuevoValor(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          agregarRebote();
                        }
                      }}
                      placeholder="0.0 - 100.0"
                      className="h-8 w-28"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={nuevoNotas}
                      onChange={(e) => setNuevoNotas(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          agregarRebote();
                        }
                      }}
                      placeholder="(opcional)"
                      className="h-8"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={agregarRebote}
                      disabled={crearReb.isPending || nuevoValor.trim() === ''}
                      title="Agregar golpe (Enter)"
                      className="h-8 w-8"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground">
        Creado por {ensayo.creado_por_email ?? 'sistema'} el{' '}
        <FechaCR fecha={ensayo.creado_en} formato="largo" />
        {ensayo.actualizado_en !== ensayo.creado_en && (
          <>
            {' '}
            · Actualizado <FechaCR fecha={ensayo.actualizado_en} formato="largo" />
          </>
        )}
      </div>
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

// =============================================================================
// FilaRebote: inline edit con onBlur (sin botones "Guardar"). El valor se
// commit-ea al perder foco si cambió. Notas igual.
// =============================================================================

interface FilaProps {
  rebote: Rebote;
  idEnsayo: number;
  puedeEscribir: boolean;
  onActualizar: (args: {
    id: number;
    idEnsayo: number;
    valor_rebote?: number;
    notas?: string | null;
  }) => void;
  onEliminar: () => void;
}

function FilaRebote(props: FilaProps) {
  const { rebote, idEnsayo, puedeEscribir, onActualizar, onEliminar } = props;
  const [valor, setValor] = useState(String(rebote.valor_rebote));
  const [notas, setNotas] = useState(rebote.notas ?? '');

  const commitValor = () => {
    const v = Number(valor);
    if (!Number.isFinite(v) || v < 0 || v > 100) {
      toast.error('Valor inválido (0-100)');
      setValor(String(rebote.valor_rebote));
      return;
    }
    if (v !== rebote.valor_rebote) {
      onActualizar({ id: rebote.id, idEnsayo, valor_rebote: v });
    }
  };

  const commitNotas = () => {
    const limpio = notas.trim();
    const actual = rebote.notas ?? '';
    if (limpio !== actual) {
      onActualizar({ id: rebote.id, idEnsayo, notas: limpio || null });
    }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-sm font-medium">{rebote.numero_golpe}</TableCell>
      <TableCell>
        {puedeEscribir ? (
          <Input
            type="number"
            step="0.1"
            min={0}
            max={100}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            onBlur={commitValor}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="h-8 w-28"
          />
        ) : (
          <span className="font-mono tabular-nums">{rebote.valor_rebote.toFixed(1)}</span>
        )}
      </TableCell>
      <TableCell>
        {puedeEscribir ? (
          <Input
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            onBlur={commitNotas}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            className="h-8"
            placeholder="(opcional)"
          />
        ) : (
          rebote.notas || <span className="italic text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        {puedeEscribir && (
          <Button
            size="icon"
            variant="ghost"
            onClick={() => {
              if (confirm(`¿Eliminar golpe #${rebote.numero_golpe}?`)) onEliminar();
            }}
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title="Eliminar golpe"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
